import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch, apiPost } from "../api";
import type { Dayjs } from "dayjs";
import type { VoucherType } from "./voucherTypes";

export type VoucherState = "active" | "depleted" | "expired" | "cancelled";
export type VoucherSource = "eshop" | "counter";
export type LookupKind = "voucher" | "bank_available" | "claim" | "unknown";
export type RideMethod = "barcode" | "manual";

export interface LookupVoucherPayload {
  id: string;
  barcode: string;
  state: VoucherState;
  source: VoucherSource;
  rides_total: number;
  rides_remaining: number;
  valid_from: Dayjs;
  valid_until: Dayjs;
  customer_name: string;
  customer_phone: string | null;
  customer_email: string | null;
  note: string | null;
  sale_price_czk: number;
  voucher_type?: VoucherType;
}

export interface LookupBankPayload {
  barcode: string;
  voucher_type?: VoucherType;
}

export interface LookupClaimItemSummary {
  id: number;
  kind: "voucher" | "merch";
  state: "pending" | "redeemed" | "cancelled";
  product_name: string;
}

export interface LookupClaimPayload {
  barcode: string;
  hint: string;
  certificate_id?: number;
  certificate_item?: LookupClaimItemSummary;
}

export interface LookupResponse {
  kind: LookupKind;
  voucher?: LookupVoucherPayload;
  bank?: LookupBankPayload;
  claim?: LookupClaimPayload;
}

export interface CreateCounterSalePayload {
  barcode: string;
  customer_name: string;
  customer_phone?: string | null;
  customer_email?: string | null;
  note?: string | null;
}

export interface VoucherResponse {
  id: string;
  barcode: string;
  voucher_type_id: number;
  rides_total: number;
  rides_remaining: number;
  valid_from: Dayjs;
  valid_until: Dayjs;
  customer_id: number | null;
  customer_name: string;
  customer_phone: string | null;
  customer_email: string | null;
  note: string | null;
  source: VoucherSource;
  sale_price_czk: number;
  state: VoucherState;
  created_at: Dayjs;
  voucher_type?: VoucherType;
}

export function useVoucherLookup() {
  return useMutation<LookupResponse, unknown, string>({
    mutationFn: (code) => apiPost<LookupResponse>("/vouchers/lookup", { code }),
  });
}

export function useCreateCounterSale() {
  const qc = useQueryClient();
  return useMutation<VoucherResponse, unknown, CreateCounterSalePayload>({
    mutationFn: (payload) => apiPost<VoucherResponse>("/vouchers", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["barcode-bank"] });
      qc.invalidateQueries({ queryKey: ["voucher-types"] });
    },
  });
}

// --------------------------------------------------------------------------
// Ride registration / cancellation
// --------------------------------------------------------------------------

export interface RideResponse {
  id: string;
  voucher_id: string;
  created_at: Dayjs;
  created_by_user_id: number | null;
  method: RideMethod;
  cancelled: boolean;
  cancelled_at: Dayjs | null;
  cancelled_by_user_id: number | null;
  cancel_reason: string | null;
}

export interface RegisterRideResponse {
  ride: RideResponse;
  rides: RideResponse[];
  voucher: VoucherResponse;
}

export interface RegisterRidePayload {
  method?: RideMethod;
  count?: number;
}

export function useRegisterRide(voucherID: string) {
  const qc = useQueryClient();
  return useMutation<RegisterRideResponse, unknown, RegisterRidePayload>({
    mutationFn: (payload) =>
      apiPost<RegisterRideResponse>(
        `/vouchers/${encodeURIComponent(voucherID)}/rides`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["voucher-detail", voucherID] });
      qc.invalidateQueries({ queryKey: ["vouchers", "search"] });
      qc.invalidateQueries({ queryKey: ["today-summary"] });
    },
  });
}

export interface CancelRidePayload {
  reason: string;
}

export function useCancelRide() {
  const qc = useQueryClient();
  return useMutation<
    RegisterRideResponse,
    unknown,
    { rideID: string; voucherID: string; reason: string }
  >({
    mutationFn: ({ rideID, reason }) =>
      apiPost<RegisterRideResponse>(
        `/rides/${encodeURIComponent(rideID)}/cancel`,
        { reason },
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["voucher-detail", vars.voucherID] });
      qc.invalidateQueries({ queryKey: ["vouchers", "search"] });
      qc.invalidateQueries({ queryKey: ["today-summary"] });
    },
  });
}

// --------------------------------------------------------------------------
// Voucher search + detail
// --------------------------------------------------------------------------

export interface SearchVouchersFilters {
  q?: string;
  state?: VoucherState | "";
  source?: VoucherSource | "";
  voucher_type_id?: number | string;
  issued_from?: string;
  issued_until?: string;
  page?: number;
  per_page?: number;
  /** When false the query is skipped entirely (no network request). */
  enabled?: boolean;
}

export interface SearchVouchersResponse {
  items: VoucherResponse[];
  page: number;
  per_page: number;
  total: number;
}

export function useSearchVouchers(filters: SearchVouchersFilters) {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  if (filters.state) params.set("state", filters.state);
  if (filters.source) params.set("source", filters.source);
  if (filters.voucher_type_id)
    params.set("voucher_type_id", String(filters.voucher_type_id));
  if (filters.issued_from) params.set("issued_from", filters.issued_from);
  if (filters.issued_until) params.set("issued_until", filters.issued_until);
  if (filters.page && filters.page > 1) params.set("page", String(filters.page));
  if (filters.per_page) params.set("per_page", String(filters.per_page));
  const qs = params.toString();
  return useQuery<SearchVouchersResponse>({
    queryKey: ["vouchers", "search", filters],
    queryFn: () => apiGet(`/vouchers${qs ? `?${qs}` : ""}`),
    enabled: filters.enabled ?? true,
  });
}

export interface EshopCertLink {
  id: number;
  redemption_code: string;
}

export interface VoucherDetailResponse {
  voucher: VoucherResponse;
  rides: RideResponse[];
  eshop_certificate?: EshopCertLink;
}

export function useVoucherDetail(voucherID: string | undefined) {
  return useQuery<VoucherDetailResponse>({
    queryKey: ["voucher-detail", voucherID],
    queryFn: () =>
      apiGet<VoucherDetailResponse>(`/vouchers/${encodeURIComponent(voucherID!)}`),
    enabled: !!voucherID,
  });
}

// --------------------------------------------------------------------------
// Manager-only admin actions (Phase 3)
// --------------------------------------------------------------------------

export interface CancelVoucherPayload {
  reason: string;
}

export function useCancelVoucher(voucherID: string) {
  const qc = useQueryClient();
  return useMutation<VoucherResponse, unknown, CancelVoucherPayload>({
    mutationFn: (payload) =>
      apiPost<VoucherResponse>(
        `/vouchers/${encodeURIComponent(voucherID)}/cancel`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["voucher-detail", voucherID] });
      qc.invalidateQueries({ queryKey: ["vouchers"] });
      qc.invalidateQueries({ queryKey: ["barcode-bank"] });
      qc.invalidateQueries({ queryKey: ["today-summary"] });
    },
  });
}

export interface UpdateVoucherContactsPayload {
  customer_name?: string;
  customer_phone?: string | null;
  customer_email?: string | null;
  note?: string | null;
}

export function useUpdateVoucherContacts(voucherID: string) {
  const qc = useQueryClient();
  return useMutation<VoucherResponse, unknown, UpdateVoucherContactsPayload>({
    mutationFn: (payload) =>
      apiPatch<VoucherResponse>(`/vouchers/${encodeURIComponent(voucherID)}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["voucher-detail", voucherID] });
      qc.invalidateQueries({ queryKey: ["vouchers"] });
    },
  });
}

export interface ExtendVoucherValidityPayload {
  valid_until: string; // ISO datetime
  reason: string;
}

export function useExtendVoucherValidity(voucherID: string) {
  const qc = useQueryClient();
  return useMutation<VoucherResponse, unknown, ExtendVoucherValidityPayload>({
    mutationFn: (payload) =>
      apiPatch<VoucherResponse>(
        `/vouchers/${encodeURIComponent(voucherID)}/validity`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["voucher-detail", voucherID] });
      qc.invalidateQueries({ queryKey: ["vouchers"] });
    },
  });
}

